// ** React Imports
import { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// ** Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import loadable from "@loadable/component";
import styled, { css } from "styled-components";

// ** Custom Components
import Block from "components/layout/Block";
import Spacer from "components/layout/Spacer";
import Panel from "components/layout/Nav/Panel";
import List from "components/layout/Nav/List";
import Item from "components/layout/Nav/Item";
import { Condition } from "components/conditional";
import PresentationTemplateContainer from "components/layout/PresentationTemplate/PresentationTemplateContainer/PresentationTemplateContainer";
import { ButtonSecondary } from "components/layout/Button/ButtonSecondary";

const ActionSection = loadable(
	() =>
		import(
			"components/layout/PresentationTemplate/ActionSection/ActionSection"
		),
);

// ** Menu Items
import { UserNavSectionProfileItem } from "./menu-items/user-nav-section-profile-item";
import { UserNavSectionGalleryItem } from "./menu-items/user-nav-section-gallery-item";
import { UserNavSectionFriendsItem } from "./menu-items/user-nav-section-friends-item";

// ** Custom Hooks
import useActionItems from "./hooks/useActionItems";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styles Import
import { breakpoints } from "styles/grid";

// ** SVG Static Imports
import ElipsisHorizontal from "components/icons/ElipsisHorizontal";

// #####################################################

const StyledNavigationContainer = styled((props) => (
	<Block flex flexWrap {...props} />
))`
	nav,
	li {
		border: 0;
	}

	@media (max-width: ${breakpoints.phone - 1}px) {
		flex: 1;

		ul {
			display: grid;
			grid-template-columns: 25% 25% 25% 25%;
			min-height: 56px;

			${({ $isAllowed }) =>
				!$isAllowed &&
				css`
					grid-template-columns: 100%;
				`}
		}
	}
`;

// #####################################################

const UserNavSection = () => {
	const { isPhone } = useContext(deviceTypeContext);

	const [isActionMenuVisible, setActionMenuVisibility] = useState(false);

	const isAllowed = useSelector((state) => state.userProfileMain.isAllowed);

	const location = useLocation();

	const actionItems = useActionItems();

	useEffect(() => {
		if (isActionMenuVisible && isPhone) {
			setActionMenuVisibility(false);
		}
	}, [location.pathname]);

	// #####################################################

	return (
		<Block>
			<PresentationTemplateContainer
				flex
				justifyBetween
				flexWrap
				alignCenter
			>
				<StyledNavigationContainer $isAllowed={isAllowed}>
					<Panel appearance="tabs" noMargin noLine>
						<List noPad>
							{isAllowed && (
								<>
									{/* Profil */}
									<UserNavSectionProfileItem />
									{/* Galeria */}
									<UserNavSectionGalleryItem />
									{/* Znajomi */}
									<UserNavSectionFriendsItem />
								</>
							)}

							{/* Menu mobile */}
							<Condition value={actionItems && isPhone}>
								<Item
									expandable
									flex
									style={{
										alignItems: "center",
										justifyContent: !isAllowed
											? "right"
											: "center",
										backgroundColor: "transparent",
										marginRight: !isAllowed ? "1rem" : 0,
									}}
								>
									<Block
										flex
										onClick={() =>
											setActionMenuVisibility(
												(prev) => !prev,
											)
										}
										style={{ cursor: "pointer" }}
									>
										<ButtonSecondary
											iconOnly
											svgIconComponent={ElipsisHorizontal}
											icon="ellipsis-h"
										/>
									</Block>
								</Item>
							</Condition>
						</List>
					</Panel>
				</StyledNavigationContainer>
				{!isPhone && <Spacer />}
				<ActionSection
					actionItems={actionItems}
					isActionMenuVisible={isActionMenuVisible}
					setActionMenuVisibility={setActionMenuVisibility}
				/>
			</PresentationTemplateContainer>
		</Block>
	);
};

// #####################################################

export default UserNavSection;

// #####################################################
